import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { printManifestoMinutaPdf } from '@/lib/manifestoMinutaPrint';
import { downloadManifestoMinutaDocx } from '@/lib/manifestoMinutaDocx';
import { entities, getDisplayMessage, uploadFile, parseUploadBlobIdFromFileUrl } from '@/api/apiClient';
import { toast } from 'sonner';
import { FileSearch, FileText, Loader2, Scale, Shield, Sparkles, FileUp, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const PHASES = [
  {
    key: 'recurso',
    title: 'Envie o Recurso',
    description:
      'Recurso do concorrente e dados da sua proposta. O edital e o perfil da empresa já entram automaticamente no contexto da IA.',
    Icon: FileSearch,
  },
  {
    key: 'ia',
    title: 'IA Analisa Argumentos',
    description: 'Identifica pontos fracos na argumentação do recorrente.',
    Icon: Scale,
  },
  {
    key: 'revisao',
    title: 'Revise as Contrarrazões',
    description: 'Selecione e personalize as refutações mais relevantes.',
    Icon: FileText,
  },
  {
    key: 'protocolo',
    title: 'Baixe e Protocole',
    description: 'Minuta editável pronta para revisão jurídica e protocolo.',
    Icon: Shield,
  },
];

const PONTOS_REFUTACAO = [
  { id: 'c1', titulo: 'Regularidade fiscal comprovada', base: 'Art. 68, Lei 14.133/2021 · Acórdão TCU 1.522/2024', fragilidade: 'Alta' },
  { id: 'c2', titulo: 'Atestado técnico compatível', base: 'Art. 5º e 67, Lei 14.133/2021 · Súmula TCU 263', fragilidade: 'Alta' },
  { id: 'c3', titulo: 'BDI dentro da faixa aceitável', base: 'Acórdão TCU 2.622/2013 · IN SEGES 5/2017', fragilidade: 'Média' },
];

function parseObservacoesContrarrazao(raw) {
  if (raw == null) return null;
  const s = typeof raw === 'string' ? raw.trim() : '';
  if (!s || s[0] !== '{') return null;
  try {
    const o = JSON.parse(s);
    if (!o || typeof o !== 'object' || o.tipo !== 'contrarrazao') return null;
    return o;
  } catch {
    return null;
  }
}

function buildObservacoesContrarrazaoV2({
  recursoAdversarioResumo,
  propostaResumo,
  referenciaNome,
  faseAtual,
  pontosSelecionados,
  resumoTexto,
}) {
  const ids = Array.isArray(pontosSelecionados) ? pontosSelecionados : [...pontosSelecionados];
  return JSON.stringify({
    v: 2,
    tipo: 'contrarrazao',
    salvoEm: new Date().toISOString(),
    fluxoContrarrazao: {
      recursoAdversarioResumo: String(recursoAdversarioResumo ?? '').trim(),
      propostaResumo: String(propostaResumo ?? '').trim(),
      referenciaNome: referenciaNome ? String(referenciaNome).trim().slice(0, 400) : null,
      faseAtual: typeof faseAtual === 'number' ? faseAtual : 0,
    },
    pontosSelecionados: ids,
    resumoTexto: resumoTexto ?? '',
  });
}

function buildMinutaFromPontos(selected) {
  const base = PONTOS_REFUTACAO.filter((p) => selected.has(p.id))
    .map((p, idx) => `${idx + 1}. ${p.titulo} (${p.base})`)
    .join('\n');
  return `I - Síntese do recurso adversário\nA parte recorrente apresenta alegações para reforma da decisão.\n\nII - Fundamentos finais da peça\n${base || 'Sem pontos selecionados.'}\n\nIII - Pedidos\n1. Não provimento do recurso adversário.\n2. Manutenção integral da decisão administrativa.`;
}

function Stepper({ currentIndex }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {PHASES.map((p, idx) => {
        const Icon = p.Icon;
        const active = idx === currentIndex;
        const done = idx < currentIndex;
        return (
          <div
            key={p.key}
            className={cn(
              'rounded-lg border p-3 text-left transition-shadow',
              active && 'ring-2 ring-blue-400 border-blue-300 shadow-md bg-blue-50/90',
              done && !active && 'bg-blue-50/50 border-blue-200',
              !done && !active && 'bg-muted/20 border-muted'
            )}
          >
            <div className="flex items-start gap-2">
              <span className="shrink-0 text-[11px] font-bold tabular-nums text-blue-800 pt-0.5">
                {String(idx + 1).padStart(2, '0')}
              </span>
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex items-center gap-1.5">
                  <Icon className={cn('h-4 w-4 shrink-0', active ? 'text-blue-700' : 'text-slate-500')} aria-hidden />
                  <p className="font-semibold text-xs leading-tight text-slate-900">{p.title}</p>
                </div>
                <p className="text-[11px] leading-snug text-slate-600">{p.description}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function ManifestoContrarrazaoFlow({
  onSalvarRascunho,
  onSalvarFinal,
  salvandoFinal = false,
  onAvancar,
  editalLabel,
  editalId,
  manifestacaoId,
}) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const hydratedKeyRef = useRef('');

  const [stepIndex, setStepIndex] = useState(0);
  const [recursoAdversarioResumo, setRecursoAdversarioResumo] = useState('');
  const [propostaResumo, setPropostaResumo] = useState('');
  const [referenciaFileUrl, setReferenciaFileUrl] = useState(null);
  const [referenciaNome, setReferenciaNome] = useState('');
  const [refUploading, setRefUploading] = useState(false);
  const [persistindoDocs, setPersistindoDocs] = useState(false);

  const [iaLoading, setIaLoading] = useState(false);
  const [iaConcluida, setIaConcluida] = useState(false);
  const [rascunhoIaTexto, setRascunhoIaTexto] = useState('');
  const [gerandoDocx, setGerandoDocx] = useState(false);

  const [selected, setSelected] = useState(() => new Set(['c1', 'c2']));
  const [minutaText, setMinutaText] = useState('');

  const canUseApi = Boolean(editalId && manifestacaoId);

  const { data: detalheServidor } = useQuery({
    queryKey: ['manifesto', 'detail', editalId, manifestacaoId],
    queryFn: () => entities.Manifesto.getManifestacao(editalId, manifestacaoId),
    enabled: canUseApi,
  });

  const rehydrateKey = useMemo(() => {
    if (!detalheServidor || String(detalheServidor.id) !== String(manifestacaoId)) return '';
    const obs = typeof detalheServidor.observacoes === 'string' ? detalheServidor.observacoes.length : 0;
    const p0 = Array.isArray(detalheServidor.pecas) && detalheServidor.pecas.length ? detalheServidor.pecas[0] : null;
    return [manifestacaoId, detalheServidor.updatedAt ?? '', obs, p0?.versao ?? '', String(p0?.conteudo ?? '').slice(0, 80)].join('|');
  }, [detalheServidor, manifestacaoId]);

  useEffect(() => {
    if (!rehydrateKey || hydratedKeyRef.current === rehydrateKey) return;
    hydratedKeyRef.current = rehydrateKey;

    const det = detalheServidor;
    if (!det) return;

    const parsed = parseObservacoesContrarrazao(det.observacoes);
    if (parsed?.v === 2 && parsed.fluxoContrarrazao && typeof parsed.fluxoContrarrazao === 'object') {
      const fc = parsed.fluxoContrarrazao;
      setRecursoAdversarioResumo(String(fc.recursoAdversarioResumo ?? ''));
      setPropostaResumo(String(fc.propostaResumo ?? ''));
      if (typeof fc.faseAtual === 'number' && fc.faseAtual >= 0 && fc.faseAtual <= 3) {
        setStepIndex(fc.faseAtual);
      }
      if (fc.referenciaNome) setReferenciaNome(String(fc.referenciaNome));
    } else if (parsed?.v === 1) {
      if (Array.isArray(parsed.pontosSelecionados) && parsed.pontosSelecionados.length) {
        setSelected(new Set(parsed.pontosSelecionados));
      }
    }

    if (Array.isArray(parsed?.pontosSelecionados) && parsed.pontosSelecionados.length) {
      setSelected(new Set(parsed.pontosSelecionados));
    }

    const peca0 = Array.isArray(det.pecas) && det.pecas.length ? det.pecas[0] : null;
    if (peca0?.conteudo && String(peca0.conteudo).trim()) {
      const txt = String(peca0.conteudo);
      setMinutaText(txt);
      if (peca0.origem === 'ia') {
        setRascunhoIaTexto(txt);
        setIaConcluida(true);
      }
    }

    const faseSalva = parsed?.fluxoContrarrazao?.faseAtual;
    if (typeof faseSalva === 'number' && faseSalva >= 2) {
      setIaConcluida(true);
    }

    const refBlob = det.referenciaUploadBlob || det.referencia_upload_blob;
    if (refBlob?.filename) {
      setReferenciaNome((n) => n || refBlob.filename);
    }
  }, [rehydrateKey, detalheServidor]);

  const resumo = useMemo(
    () => `Contrarrazão — ${selected.size} refutação(ões) relevante(s) selecionada(s).`,
    [selected]
  );

  useEffect(() => {
    if (stepIndex !== 3) return;
    const t = window.setTimeout(() => {
      document.getElementById('manifesto-etapa-protocolo-contrarrazao')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 150);
    return () => window.clearTimeout(t);
  }, [stepIndex]);

  const persistDocumentos = useCallback(
    async (faseAtual = 0) => {
      if (!canUseApi) {
        toast.error('Manifestação não identificada.');
        return false;
      }
      const obs = buildObservacoesContrarrazaoV2({
        recursoAdversarioResumo,
        propostaResumo,
        referenciaNome,
        faseAtual,
        pontosSelecionados: selected,
        resumoTexto: resumo,
      });
      const patch = { observacoes: obs };
      if (referenciaFileUrl) {
        const bid = parseUploadBlobIdFromFileUrl(referenciaFileUrl);
        if (bid) patch.referenciaUploadBlobId = bid;
      }
      setPersistindoDocs(true);
      try {
        await entities.Manifesto.patchManifestacao(editalId, manifestacaoId, patch);
        await queryClient.invalidateQueries({ queryKey: ['manifesto', 'detail', editalId, manifestacaoId] });
        return true;
      } catch (e) {
        toast.error(getDisplayMessage(e, 'Não foi possível gravar os dados da fase.'));
        return false;
      } finally {
        setPersistindoDocs(false);
      }
    },
    [
      canUseApi,
      editalId,
      manifestacaoId,
      recursoAdversarioResumo,
      propostaResumo,
      referenciaNome,
      referenciaFileUrl,
      selected,
      queryClient,
      resumo,
    ]
  );

  const executarIa = useCallback(async () => {
    if (!canUseApi) {
      toast.error('Manifestação não identificada.');
      return;
    }
    setIaLoading(true);
    try {
      await persistDocumentos(1);
      const out = await entities.Manifesto.gerarRascunhoIa(editalId, manifestacaoId);
      const conteudo = out?.peca?.conteudo ?? out?.manifestacao?.pecas?.[0]?.conteudo;
      if (typeof conteudo === 'string' && conteudo.trim()) {
        setRascunhoIaTexto(conteudo.trim());
        setIaConcluida(true);
        toast.success('Análise concluída. Revise as refutações sugeridas na próxima fase.');
      } else {
        toast.error('A IA não retornou texto utilizável.');
      }
      await queryClient.invalidateQueries({ queryKey: ['manifesto', 'detail', editalId, manifestacaoId] });
    } catch (e) {
      toast.error(getDisplayMessage(e, 'Falha na análise com IA.'));
    } finally {
      setIaLoading(false);
    }
  }, [canUseApi, editalId, manifestacaoId, persistDocumentos, queryClient]);

  async function handleReferenciaFile(ev) {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith('.pdf')) {
      toast.error('Envie PDF (recurso do concorrente ou documento de referência).');
      return;
    }
    setRefUploading(true);
    try {
      const { file_url } = await uploadFile(file);
      if (!file_url) throw new Error('Upload sem URL');
      setReferenciaFileUrl(file_url);
      setReferenciaNome(file.name);
      toast.success('PDF anexado. Será vinculado ao avançar ou ao rodar a IA.');
    } catch (err) {
      toast.error(getDisplayMessage(err, 'Falha no envio do arquivo.'));
    } finally {
      setRefUploading(false);
    }
  }

  function podeAvancarDaFaseRecurso() {
    const r = recursoAdversarioResumo.trim();
    const p = propostaResumo.trim();
    return r.length >= 20 && p.length >= 20;
  }

  async function handleAvancar() {
    if (stepIndex === 0) {
      if (!podeAvancarDaFaseRecurso()) {
        toast.error('Descreva o recurso do concorrente e os dados da sua proposta (mínimo sugerido: 20 caracteres em cada).');
        return;
      }
      const ok = await persistDocumentos(1);
      if (!ok) return;
      setStepIndex(1);
      if (typeof onAvancar === 'function') onAvancar(1);
      return;
    }
    if (stepIndex === 1) {
      if (!iaConcluida) {
        toast.error('Execute a análise com IA antes de avançar.');
        return;
      }
      const ok = await persistDocumentos(2);
      if (!ok) return;
      setStepIndex(2);
      if (typeof onAvancar === 'function') onAvancar(2);
      return;
    }
    if (stepIndex === 2) {
      if (selected.size === 0) {
        toast.error('Selecione ao menos uma linha de refutação.');
        return;
      }
      const ok = await persistDocumentos(3);
      if (!ok) return;
      setMinutaText((prev) => {
        const cur = String(prev ?? '').trim();
        if (cur) return prev;
        const ia = String(rascunhoIaTexto ?? '').trim();
        if (ia) return ia;
        return buildMinutaFromPontos(selected);
      });
      setStepIndex(3);
      if (typeof onAvancar === 'function') onAvancar(3);
    }
  }

  function handleVoltar() {
    if (stepIndex <= 0) return;
    setStepIndex((i) => i - 1);
  }

  const faseTitulo = PHASES[stepIndex]?.title ?? '';
  const faseDescricao = PHASES[stepIndex]?.description ?? '';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Scale className="h-5 w-5 text-blue-600" />
          Contrarrazão
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Stepper currentIndex={stepIndex} />

        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
          <p className="text-xs font-semibold text-blue-900">
            Fase {stepIndex + 1} de {PHASES.length} — {faseTitulo}
          </p>
          <p className="text-sm mt-1 text-slate-800">{faseDescricao}</p>
        </div>

        {!canUseApi ? (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
            Selecione uma manifestação de contrarrazão na lista para habilitar o fluxo completo.
          </p>
        ) : null}

        {stepIndex === 0 ? (
          <div className="space-y-4 rounded-lg border bg-card p-4">
            <div>
              <Label className="text-xs font-semibold text-slate-700">Edital (contexto do certame)</Label>
              <p className="text-sm mt-1 text-slate-800 rounded-md border bg-muted/30 px-3 py-2">
                {editalLabel?.trim() || '—'}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Os dados do edital e da empresa cadastrados no sistema são enviados automaticamente para a IA nas fases seguintes.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="contrarrazao-recurso-adversario">Recurso do concorrente (fatos e argumentos a refutar)</Label>
              <Textarea
                id="contrarrazao-recurso-adversario"
                rows={5}
                className="text-sm"
                placeholder="Resuma ou transcreva os principais fundamentos do recurso administrativo do concorrente."
                value={recursoAdversarioResumo}
                onChange={(e) => setRecursoAdversarioResumo(e.target.value)}
                disabled={!canUseApi}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contrarrazao-proposta">Dados da sua proposta e da decisão que defende</Label>
              <Textarea
                id="contrarrazao-proposta"
                rows={5}
                className="text-sm"
                placeholder="Descreva sua proposta, classificação, regularidade e o ato administrativo que deve ser mantido."
                value={propostaResumo}
                onChange={(e) => setPropostaResumo(e.target.value)}
                disabled={!canUseApi}
              />
            </div>
            <div className="rounded-md border border-slate-200 p-3 space-y-2">
              <Label className="text-xs font-semibold">PDF do recurso do concorrente (opcional)</Label>
              <p className="text-xs text-slate-600">
                Anexe o PDF do recurso ou trechos relevantes para enriquecer a extração de texto na análise com IA.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                className="hidden"
                onChange={handleReferenciaFile}
              />
              {referenciaNome ? (
                <div className="flex items-center justify-between gap-2 text-xs rounded bg-slate-50 px-2 py-1.5">
                  <span className="truncate" title={referenciaNome}>
                    {refUploading ? (
                      <span className="flex items-center gap-1 text-slate-500">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Enviando…
                      </span>
                    ) : (
                      referenciaNome
                    )}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    disabled={refUploading}
                    onClick={() => {
                      setReferenciaFileUrl(null);
                      setReferenciaNome('');
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-slate-500" />
                  </Button>
                </div>
              ) : null}
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!canUseApi || refUploading}
                onClick={() => fileInputRef.current?.click()}
              >
                <FileUp className="h-4 w-4 mr-2" />
                {referenciaNome ? 'Substituir PDF' : 'Anexar PDF'}
              </Button>
            </div>
          </div>
        ) : null}

        {stepIndex === 1 ? (
          <div className="space-y-4 rounded-lg border bg-card p-4">
            <p className="text-sm text-slate-700">
              A IA utiliza o edital e o perfil da empresa já cadastrados, os textos que você informou e o PDF anexo (se
              houver) para analisar a argumentação do recorrente e gerar um rascunho de contrarrazão.
            </p>
            <Button type="button" className="gap-2" disabled={!canUseApi || iaLoading} onClick={() => executarIa()}>
              {iaLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Analisar argumentos com IA
            </Button>
            {iaConcluida ? (
              <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900">
                Análise concluída. Na próxima fase, selecione as refutações; em seguida edite a minuta completa para
                exportação.
              </div>
            ) : null}
            {rascunhoIaTexto.trim() ? (
              <details className="rounded-md border bg-muted/20 p-3 text-sm">
                <summary className="cursor-pointer font-medium text-slate-800">Prévia do rascunho gerado (trecho)</summary>
                <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap text-xs text-slate-700 font-mono">
                  {rascunhoIaTexto.slice(0, 4000)}
                  {rascunhoIaTexto.length > 4000 ? '\n…' : ''}
                </pre>
              </details>
            ) : null}
          </div>
        ) : null}

        {stepIndex === 2 ? (
          <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
            <div className="grid gap-3">
              <p className="text-xs text-slate-600">
                Pontos típicos de fragilidade na argumentação do recorrente — marque os que melhor se aplicam e ajuste o
                texto na minuta na fase seguinte.
              </p>
              {PONTOS_REFUTACAO.map((item) => {
                const checked = selected.has(item.id);
                return (
                  <label key={item.id} className="rounded-lg border p-3 block cursor-pointer">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(v) => {
                            const next = new Set(selected);
                            if (v) next.add(item.id);
                            else next.delete(item.id);
                            setSelected(next);
                          }}
                        />
                        <div>
                          <p className="font-medium text-sm">{item.titulo}</p>
                          <p className="text-xs text-slate-600 mt-1">{item.base}</p>
                          <p className="text-xs text-slate-500 mt-2">Personalize na minuta (fase 04) se necessário.</p>
                        </div>
                      </div>
                      <Badge variant={item.fragilidade === 'Alta' ? 'destructive' : 'secondary'}>
                        Fragilidade {item.fragilidade}
                      </Badge>
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="space-y-3">
              <div className="rounded-lg border bg-slate-50 p-3">
                <p className="text-sm font-medium">Estratégia da defesa</p>
                <p className="text-xs text-slate-600 mt-2">
                  Manter a decisão demonstrando regularidade, aderência ao edital e ausência de prejuízo à competitividade.
                </p>
              </div>
              <div className="rounded-lg border bg-slate-50 p-3">
                <p className="text-sm font-medium">Checklist</p>
                <ul className="text-xs text-slate-600 mt-2 space-y-1">
                  <li>✓ Tempestividade</li>
                  <li>✓ Refutação fundamentada</li>
                  <li>✓ Pedido de não provimento</li>
                  <li>✓ Manutenção do resultado</li>
                </ul>
              </div>
            </div>
          </div>
        ) : null}

        {stepIndex === 3 ? (
          <div className="space-y-4">
            <div className="rounded-lg border bg-muted/20 p-3">
              <p className="text-sm font-medium">Minuta da contrarrazão (editável)</p>
              <p className="text-xs text-slate-600 mt-1">
                Revise o texto integral antes de exportar. Pode incorporar o rascunho da IA e as refutações selecionadas.
              </p>
              <Textarea
                rows={12}
                className="mt-3 text-sm font-mono"
                value={minutaText}
                onChange={(e) => setMinutaText(e.target.value)}
                placeholder="Fundamentos, refutação ponto a ponto e pedidos…"
              />
            </div>
            <div
              id="manifesto-etapa-protocolo-contrarrazao"
              className="scroll-mt-6 rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-3"
            >
              <p className="text-sm font-medium">Baixe e protocole</p>
              <p className="text-xs text-slate-700">
                Gere PDF para impressão ou Word para ajustes finais e protocolo no prazo.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={!minutaText.trim()}
                  onClick={async () => {
                    try {
                      await printManifestoMinutaPdf({
                        tipoLabel: 'Contrarrazão',
                        etapaLabel: 'Protocolo',
                        editalLabel,
                        minutaText,
                      });
                    } catch (e) {
                      toast.error(e?.message || 'Erro ao imprimir minuta.');
                    }
                  }}
                >
                  Imprimir / PDF
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={!minutaText.trim() || gerandoDocx}
                  className="gap-2"
                  onClick={async () => {
                    setGerandoDocx(true);
                    try {
                      await downloadManifestoMinutaDocx(minutaText, {
                        editalLabel,
                        tipoSuffix: 'contrarrazao',
                      });
                    } catch (e) {
                      toast.error(e?.message || 'Erro ao gerar DOCX.');
                    } finally {
                      setGerandoDocx(false);
                    }
                  }}
                >
                  {gerandoDocx ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Baixar DOCX
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        <div className="flex flex-col gap-3 border-t pt-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <p className="min-w-0 w-full flex-1 text-xs text-slate-600 break-words [overflow-wrap:anywhere] leading-relaxed sm:pr-2">
            {resumo}
          </p>
          <div className="flex w-full shrink-0 flex-wrap gap-2 sm:w-auto sm:justify-end">
            <Button type="button" variant="outline" size="sm" onClick={handleVoltar} disabled={stepIndex <= 0}>
              Voltar
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={onSalvarRascunho}>
              Salvar rascunho
            </Button>
            <Button
              type="button"
              size="sm"
              className="gap-2"
              disabled={salvandoFinal || typeof onSalvarFinal !== 'function'}
              onClick={async () => {
                if (typeof onSalvarFinal !== 'function') return;
                if (!minutaText.trim() && selected.size === 0) {
                  toast.error('Avance até a minuta ou selecione refutações antes de salvar.');
                  return;
                }
                try {
                  await onSalvarFinal({
                    minutaConteudo: minutaText,
                    observacoesPayload: {
                      v: 2,
                      tipo: 'contrarrazao',
                      salvoEm: new Date().toISOString(),
                      fluxoContrarrazao: {
                        recursoAdversarioResumo: String(recursoAdversarioResumo ?? '').trim(),
                        propostaResumo: String(propostaResumo ?? '').trim(),
                        referenciaNome: referenciaNome ? String(referenciaNome).trim().slice(0, 400) : null,
                        faseAtual: stepIndex,
                      },
                      pontosSelecionados: [...selected],
                      resumoTexto: resumo,
                    },
                  });
                } catch {
                  /* toast no pai */
                }
              }}
            >
              {salvandoFinal ? <Loader2 className="h-4 w-4 animate-spin shrink-0" /> : null}
              Salvar
            </Button>
            {stepIndex < 3 ? (
              <Button
                type="button"
                size="sm"
                onClick={handleAvancar}
                disabled={
                  (stepIndex === 0 && !podeAvancarDaFaseRecurso()) ||
                  persistindoDocs ||
                  (stepIndex === 1 && !iaConcluida)
                }
              >
                {stepIndex === 0 && persistindoDocs ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {stepIndex === 0 ? 'Continuar para IA' : stepIndex === 1 ? 'Continuar para revisão' : 'Continuar para minuta'}
              </Button>
            ) : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
